import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { regionalTeamRosterService } from '../../services/regionalTeamRosterService';
import { tournamentService } from '../../services/tournamentService';
import { getRegionLabel } from '../../utils/tanzaniaRegions';

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon name={icon} size={16} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-foreground font-medium break-words">{value ?? 'N/A'}</p>
    </div>
  </div>
);

const formatPosition = (position) => {
  if (!position || position === 'other') return 'N/A';
  return position.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const formatDate = (isoDate) => {
  if (!isoDate) return 'N/A';
  return new Date(isoDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const RegionalPlayerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTeamId = searchParams.get('team');
  const fromRegionId = searchParams.get('from');

  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPlayer = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await regionalTeamRosterService.getPlayerById(id);
      setPlayer(data);
      document.title = `${data?.fullName || 'Player'} - Tanzania Basketball Federation`;

      try {
        const allStats = await tournamentService.getRegionalPlayerStatistics();
        const playerStats = (allStats || []).find((row) => row.playerId === id);
        setStats(playerStats || null);
      } catch {
        setStats(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load player');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (fromTeamId) {
      const qs = fromRegionId ? `?from=${fromRegionId}&tab=roster` : '?tab=roster';
      navigate(`/regional-teams/${fromTeamId}${qs}`);
      return;
    }
    navigate(-1);
  };

  const hasPhoto =
    player?.photoUrl &&
    typeof player.photoUrl === 'string' &&
    !player.photoUrl.includes('no_image.png');

  const ppg =
    stats?.gamesPlayed > 0 ? Number((stats.points / stats.gamesPlayed).toFixed(1)) : null;
  const rpg =
    stats?.gamesPlayed > 0 ? Number((stats.rebounds / stats.gamesPlayed).toFixed(1)) : null;
  const apg =
    stats?.gamesPlayed > 0 ? Number((stats.assists / stats.gamesPlayed).toFixed(1)) : null;

  return (
    <PublicBrowseShell>
      <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={handleBack} className="mb-6">
        {fromTeamId ? 'Back to Team Roster' : 'Back'}
      </Button>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading player...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : !player ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="User" size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Player not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border card-shadow p-6 flex items-center gap-6 flex-wrap">
            <button
              type="button"
              disabled={!hasPhoto}
              onClick={() => hasPhoto && setPhotoPreviewOpen(true)}
              className={`w-24 h-24 rounded-full bg-teal-600/10 flex items-center justify-center overflow-hidden shrink-0 border border-border ${
                hasPhoto
                  ? 'cursor-pointer hover:ring-2 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary'
                  : ''
              }`}
              title={hasPhoto ? 'View photo' : undefined}
            >
              {hasPhoto ? (
                <Image src={player.photoUrl} alt={player.fullName} className="w-full h-full object-cover" />
              ) : (
                <Icon name="User" size={40} className="text-teal-600" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">{player.fullName || 'Unknown Player'}</h1>
              <p className="text-muted-foreground">{player.teamName || 'Unassigned'}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary">
                  {formatPosition(player.position)}
                </span>
                {player.jerseyNumber != null && player.jerseyNumber !== '' && (
                  <span className="text-xs px-3 py-1 rounded-full bg-accent/10 text-accent-foreground">
                    #{player.jerseyNumber}
                  </span>
                )}
                {player.teamRegion && (
                  <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground">
                    {getRegionLabel(player.teamRegion)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Player Bio</h2>
              <div className="space-y-4">
                <InfoRow icon="Cake" label="Date of Birth" value={formatDate(player.dateOfBirth)} />
                <InfoRow icon="Hash" label="Age" value={player.age} />
                <InfoRow icon="Mail" label="Email" value={player.email} />
                <InfoRow icon="Phone" label="Phone" value={player.phone} />
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Team</h2>
              <div className="space-y-4">
                <InfoRow icon="Shield" label="Regional Team" value={player.teamName} />
                <InfoRow icon="MapPin" label="Region" value={getRegionLabel(player.teamRegion)} />
                <InfoRow
                  icon="BadgeCheck"
                  label="Team Status"
                  value={(player.teamStatus || 'pending').replace(/_/g, ' ')}
                />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border card-shadow p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Tournament Statistics</h2>
            {stats && (stats.gamesPlayed > 0 || stats.points > 0) ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-foreground">{stats.gamesPlayed ?? 0}</p>
                  <p className="text-xs text-muted-foreground">GP</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-foreground">{stats.points ?? 0}</p>
                  <p className="text-xs text-muted-foreground">PTS</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-foreground">{stats.rebounds ?? 0}</p>
                  <p className="text-xs text-muted-foreground">REB</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-foreground">{stats.assists ?? 0}</p>
                  <p className="text-xs text-muted-foreground">AST</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-foreground">{ppg ?? '-'}</p>
                  <p className="text-xs text-muted-foreground">PPG</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-foreground">
                    {rpg != null || apg != null ? `${rpg ?? '-'} / ${apg ?? '-'}` : '-'}
                  </p>
                  <p className="text-xs text-muted-foreground">RPG / APG</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No tournament statistics recorded yet.</p>
            )}
          </div>
        </div>
      )}

      {photoPreviewOpen && hasPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPhotoPreviewOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Player photo"
        >
          <div
            className="relative max-w-lg w-full bg-card rounded-xl border border-border shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
              <p className="font-medium text-foreground truncate">{player.fullName}</p>
              <button
                type="button"
                onClick={() => setPhotoPreviewOpen(false)}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                aria-label="Close photo"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="bg-muted flex items-center justify-center p-4 min-h-[280px]">
              <Image
                src={player.photoUrl}
                alt={player.fullName}
                className="max-h-[70vh] w-auto max-w-full object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default RegionalPlayerDetailPage;
